import React from 'react';
import withStyles from 'isomorphic-style-loader/withStyles';
import Hero from '../../components/marketing/Hero';
import CtaBanner from '../../components/marketing/CtaBanner';
import Link from '../../components/Link';
import s from './GiftCards.css';

// BestHand Gift Cards — ADDITIVE "coming soon" landing.
// HONESTY: there is NO gift-card system yet — NO purchase/checkout, NO balance, NO redemption flow.
// This page only pitches the idea and captures interest. The "Notify me" form is a thin client-side
// affordance that routes to /contact-us (or a mailto) — it does NOT process a purchase or store a balance.
// LEGAL_REVIEW_REQUIRED: all value, terms and gift-card copy below is non-final marketing copy and must be
// reviewed by counsel before production (gift-card programs are heavily regulated — expiry, fees,
// escheatment and state law all apply before any real launch).

const PITCH = [
  {
    icon: 'M20 12v9H4v-9M2 7h20v5H2zM12 22V7M12 7H7.5a2.5 2.5 0 0 1 0-5C11 2 12 7 12 7ZM12 7h4.5a2.5 2.5 0 0 0 0-5C13 2 12 7 12 7Z',
    title: 'The gift of getting it done',
    text: 'Give friends and family a trusted local pro for repairs, cleaning, mounting, moving help and more.',
  },
  {
    icon: 'M12 2 4 6v6c0 5 3.4 8 8 10 4.6-2 8-5 8-10V6l-8-4ZM9 12l2 2 4-4',
    title: 'Vetted pros included',
    // COMPLIANCE: background-check claim is intentional (founder confirms BestHand performs background checks). LEGAL_REVIEW_REQUIRED before prod.
    text: 'Every BestHand pro is background-checked — a gift you can feel good about.',
  },
  {
    icon: 'M2 10h20M6 15h4m8-9H6a4 4 0 0 0-4 4v8a4 4 0 0 0 4 4h12a4 4 0 0 0 4-4V10a4 4 0 0 0-4-4Z',
    title: 'Simple and secure',
    text: 'When gift cards launch, they’ll be easy to send and redeem, with the same secure, cashless payments.',
  },
];

// JSON-LD intentionally minimal: an Organization reference only. We do NOT mark up a buyable Product/Offer
// because gift cards are not for sale yet (avoid implying a purchasable item to search engines).
const jsonLd = {
  '@context': 'https://schema.org',
  '@type': 'Organization',
  name: 'BestHand',
  url: 'https://besthandapp.com',
};

function GiftCards() {
  const [email, setEmail] = React.useState('');

  // No real signup backend yet — route the interest to support via a prefilled mailto. SSR-safe (mailto
  // string is computed at render; no window access).
  const mailto = `mailto:support@besthandapp.com?subject=${encodeURIComponent(
    'Notify me when BestHand gift cards launch',
  )}&body=${encodeURIComponent(
    `Please notify me when BestHand gift cards are available.${email ? `\n\nMy email: ${email}` : ''}`,
  )}`;

  return (
    <main className={s.page}>
      {/* SECURITY: escape </script> breakouts in JSON-LD (defense-in-depth). */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd).replace(/</g, '\\u003c').replace(/>/g, '\\u003e').replace(/&/g, '\\u0026') }}
      />

      <Hero
        eyebrow="Gift Cards · Coming soon"
        title="Give the gift of a trusted pro."
        subtitle="BestHand gift cards are on the way. Soon you’ll be able to give friends and family help with repairs, cleaning, mounting and more — from vetted local pros."
        badges={['Coming soon', 'Vetted pros', 'Secure payments']}
      />

      {/* Coming-soon banner */}
      <section className={s.soonSec}>
        <div className={s.wrap}>
          <div className={s.soonBadge}>
            <span className={s.soonDot} aria-hidden="true" />
            Coming soon
          </div>
          <p className={s.soonText}>
            Gift cards aren’t available to purchase just yet. We’re building a simple, secure way to send and
            redeem them — leave your email and we’ll let you know the moment they launch.
          </p>
        </div>
      </section>

      {/* Pitch */}
      <section className={`${s.sec} ${s.alt}`}>
        <div className={s.wrap}>
          <h2 className={s.h2}>Why a BestHand gift card?</h2>
          <div className={s.grid}>
            {PITCH.map((p) => (
              <div className={s.card} key={p.title}>
                <span className={s.cardIc}>
                  <svg viewBox="0 0 24 24" aria-hidden="true">
                    <path d={p.icon} fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </span>
                <h3 className={s.cardTitle}>{p.title}</h3>
                <p className={s.cardText}>{p.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Notify me — NOT a checkout. Just interest capture. */}
      <section className={s.sec}>
        <div className={s.wrap}>
          <div className={s.notifyCard}>
            <h2 className={s.h2Left}>Be the first to know</h2>
            <p className={s.notifyText}>
              Drop your email and we’ll notify you when gift cards go live. No purchase happens here yet.
            </p>
            <form
              className={s.notifyForm}
              action={mailto}
              method="get"
              onSubmit={(e) => {
                // Hand off to the mailto interest path; no real submission/checkout occurs.
                e.preventDefault();
                if (typeof window !== 'undefined') window.location.href = mailto;
              }}
            >
              <label htmlFor="gc-email" className={s.srOnly}>Your email</label>
              <input
                id="gc-email"
                type="email"
                className={s.notifyInput}
                placeholder="you@example.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                autoComplete="email"
              />
              <button type="submit" className={s.notifyBtn}>Notify me</button>
            </form>
            <p className={s.notifyNote}>
              Prefer to reach a human?{' '}
              <Link to="/contact-us" className={s.inlineLink}>Contact us</Link> and we’ll keep you posted.
            </p>
          </div>
        </div>
      </section>

      <CtaBanner
        title="In the meantime, book a trusted pro."
        subtitle="Vetted local pros with upfront, industry-low pricing."
        primaryLabel="Browse services"
        primaryTo="/services"
        secondaryLabel="See how it works"
        secondaryTo="/how-it-works"
      />
    </main>
  );
}

export default withStyles(s)(GiftCards);
